import { useEffect, useRef, useState, useCallback } from "react";
import * as signalR from "@microsoft/signalr";
import { HIDE_DEMO_KEY } from "../utils/request";

const CHAT_URL = process.env.REACT_APP_CHAT_URL || "http://localhost:6003/hub/chat";

/**
 * 連線網址要帶 hideDemo，因為 SignalR 沒辦法帶自訂標頭 ——
 * 瀏覽器的 WebSocket API 根本沒有設定標頭的介面，所以 web 那套
 * X-Hide-Demo 標頭在這裡行不通。ChatHub 會從 query string 讀。
 *
 * 值只在建立連線時決定；切換示範資料開關時偏好設定頁會整頁重載，
 * 連線本來就會重建，所以不必額外處理。
 */
function chatUrl() {
  // 與 utils/request.js 同一套預設：沒設定過就隱藏，只有明確選「顯示」（"0"）才不帶
  if (localStorage.getItem(HIDE_DEMO_KEY) === "0") return CHAT_URL;
  return CHAT_URL + (CHAT_URL.includes("?") ? "&" : "?") + "hideDemo=1";
}

export default function useChat() {
  const [messages, setMessages] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [connected, setConnected] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const connectionRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("TOKEN");
    if (!token) return;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(chatUrl(), {
        accessTokenFactory: () => localStorage.getItem("TOKEN"),
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connectionRef.current = connection;

    // 收到歷史訊息
    /* 後端改回 { items, has_more }；舊格式（純陣列）也照吃，避免版本落差時聊天室整片空白 */
    connection.on("ChatHistory", (payload) => {
      const items = Array.isArray(payload) ? payload : (payload?.items || []);
      const more = Array.isArray(payload) ? items.length >= 50 : !!payload?.has_more;
      setMessages(items.map(normalizeMsg));
      setHasMore(more);
    });

    // 收到新訊息
    connection.on("ReceiveMessage", (msg) => {
      setMessages((prev) => [...prev, normalizeMsg(msg)]);
      setNewMessageCount((n) => n + 1);
    });

    // 訊息被收回
    connection.on("MessageRecalled", ({ id, name }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === id ? { ...m, content: "", is_recalled: true } : m
        )
      );
    });

    // 收到更舊的訊息
    connection.on("OlderMessages", (payload) => {
      const items = Array.isArray(payload) ? payload : (payload?.items || []);
      const more = Array.isArray(payload) ? items.length >= 30 : !!payload?.has_more;
      if (items.length === 0) {
        setHasMore(false);
      } else {
        setMessages((prev) => [...items.map(normalizeMsg), ...prev]);
        setHasMore(more);
      }
      setLoadingMore(false);
    });

    // 線上用戶列表
    connection.on("OnlineUsers", (users) => {
      setOnlineUsers(users);
    });

    // 有人上線
    connection.on("UserJoined", (user) => {
      setOnlineUsers((prev) => {
        if (prev.some((u) => u.account === user.account)) return prev;
        return [...prev, user];
      });
    });

    // 有人離線
    connection.on("UserLeft", (user) => {
      setOnlineUsers((prev) => prev.filter((u) => u.account !== user.account));
    });

    connection.onreconnecting(() => setConnected(false));
    connection.onreconnected(() => setConnected(true));
    connection.onclose(() => setConnected(false));

    /*
     * 心跳。
     *
     * 在線名單原本只靠 SignalR 的斷線事件維護，但那件事不保證會發生 ——
     * 連線被代理掐斷、電腦闔蓋睡著、服務被回收，都會在伺服器留下一筆
     * 沒人來收的連線，那個帳號就「永遠在線上」。每 30 秒打一次，
     * 伺服器容忍 3 分鐘沒心跳才判離線（漏五次才算數，網路抖一下不會誤判）。
     */
    let beat = null;
    const ping = () => {
      if (connection.state === signalR.HubConnectionState.Connected) {
        connection.invoke("Heartbeat").catch(() => {});
      }
    };

    connection
      .start()
      .then(() => {
        setConnected(true);
        beat = setInterval(ping, 30000);
      })
      .catch((err) => console.error("[Chat] Connection failed:", err));

    return () => {
      if (beat) clearInterval(beat);
      connection.stop();
    };
  }, []);

  const sendMessage = useCallback((content) => {
    if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
      connectionRef.current.invoke("SendMessage", content).catch(console.error);
    }
  }, []);

  const recallMessage = useCallback((messageId) => {
    if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
      connectionRef.current.invoke("RecallMessage", messageId).catch(console.error);
    }
  }, []);

  const loadMoreMessages = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    setMessages((prev) => {
      if (prev.length === 0) { setLoadingMore(false); return prev; }
      const oldestId = prev[0].id;
      if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
        connectionRef.current.invoke("LoadMoreMessages", oldestId).catch(() => setLoadingMore(false));
      } else {
        setLoadingMore(false);
      }
      return prev;
    });
  }, [loadingMore, hasMore]);

  const resetNewCount = useCallback(() => setNewMessageCount(0), []);

  return { messages, onlineUsers, connected, sendMessage, recallMessage, loadMoreMessages, loadingMore, hasMore, newMessageCount, resetNewCount };
}

function normalizeMsg(msg) {
  const d = new Date(msg.time);
  return {
    id: msg.id,
    account: msg.account,
    name: msg.name,
    content: msg.content,
    is_recalled: msg.is_recalled || false,
    date: `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`,
    time: d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
  };
}
